import React, { useEffect, useState } from "react";
import { Empty } from "./OverviewTab";
import { ArrowRight, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { useT } from "../../lib/i18n";
import { formatApiError } from "../../lib/api";

const DEFAULT_CLINICAL_CONTEXT = {
  has_clinical_data: "no",
  literature_review: "no",
  equivalent_device: "no",
  equivalence_clinical: "no",
  equivalence_technical: "no",
  equivalence_biological: "no",
  pmcf_plan: "no",
  target_population: "",
  intended_clinical_benefit: "",
};

export default function ClinicalTab({ version, onReevaluate, focusFieldOnOpen = "", onFocusHandled }) {
  const t = useT();
  const clinicalContext = version?.intake?.clinical_context;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(DEFAULT_CLINICAL_CONTEXT);
  const [focusField, setFocusField] = useState("");
  const [reevaluating, setReevaluating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft({
      ...DEFAULT_CLINICAL_CONTEXT,
      ...(clinicalContext || {}),
    });
    setEditing(false);
    setFocusField("");
    setError("");
  }, [version?.version, clinicalContext]);

  useEffect(() => {
    if (!editing || !focusField) return;
    window.setTimeout(() => {
      const field = document.getElementById(`clinical-fix-${focusField}`);
      field?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      field?.focus();
    }, 0);
  }, [editing, focusField]);

  useEffect(() => {
    if (!focusFieldOnOpen) return;
    setEditing(true);
    setFocusField(focusFieldOnOpen);
    onFocusHandled?.();
  }, [focusFieldOnOpen, onFocusHandled]);

  if (!version) return <Empty msg={t("ov.no_analysis")} />;

  const c = version.clinical || {};
  const gaps = Array.isArray(c.gaps) ? c.gaps : [];
  const flags = Array.isArray(c.flags) ? c.flags : [];
  const issues = [
    ...gaps.map((gap, i) => ({
      key: `gap-${i}`,
      topic: gap.topic || t("clinical.gaps_title"),
      issue: gap.issue || "",
      reference: gap.mdr_article || "",
      target: targetForGap(gap.topic),
    })),
    ...flags.map((flag, i) => ({
      key: `flag-${i}`,
      topic: t("clinical.flags"),
      issue: flag,
      reference: "",
      target: "pmcf_plan",
    })),
  ];
  if (!issues.length && (c.data_sufficiency === "partial" || c.data_sufficiency === "insufficient" || c.article_61_compliance === false)) {
    issues.push({
      key: "status",
      topic: t("clinical.data_sufficiency"),
      issue: t("clinical.review_status"),
      reference: "MDR Article 61",
      target: "has_clinical_data",
    });
  }

  function updateField(name, value) {
    setDraft((current) => ({ ...current, [name]: value }));
    setError("");
  }

  function openFix(target) {
    setFocusField(target);
    setEditing(true);
    setError("");
  }

  async function reevaluate() {
    if (!onReevaluate) return;
    setReevaluating(true);
    setError("");
    try {
      const completed = await onReevaluate({ ...draft });
      if (completed) setEditing(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail || e.message || t("clinical.reevaluate_error")));
    } finally {
      setReevaluating(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <Stat label={t("clinical.data_sufficiency")} value={c.data_sufficiency || "—"} highlight={c.data_sufficiency} />
        <Stat label={t("clinical.equivalence_route")} value={String(c.equivalence_route || "—").replaceAll("_", " ")} />
        <Stat label={t("clinical.article61")} value={c.article_61_compliance ? t("clinical.compliant") : t("clinical.non_compliant")} ok={c.article_61_compliance} />
      </div>

      <div className="grid-card p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{t("clinical.gaps_title")}</div>
        {!issues.length ? (
          <div className="flex items-center gap-2 text-emerald-600 text-sm"><CheckCircle weight="fill" /> {t("clinical.no_gaps")}</div>
        ) : (
          <ul className="space-y-2">
            {issues.map((issue, i) => (
              <li key={issue.key} className="flex flex-col sm:flex-row sm:items-start gap-3 border border-zinc-100 p-3" data-testid={`clinical-gap-${i}`}>
                <WarningCircle weight="fill" className={gaps[i]?.severity === "high" ? "text-red-600 shrink-0 mt-0.5" : "text-amber-500 shrink-0 mt-0.5"} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{issue.topic}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">{issue.issue}</div>
                  {issue.reference && <div className="font-mono text-[10px] text-zinc-400 mt-1">{issue.reference}</div>}
                </div>
                <button
                  type="button"
                  onClick={() => openFix(issue.target)}
                  data-testid={`clinical-solve-${i}`}
                  className="inline-flex items-center justify-center gap-1.5 shrink-0 border border-blue-200 bg-blue-50 hover:bg-blue-100 text-blue-800 px-3 py-2 rounded-sm text-xs font-semibold"
                >
                  {t("clinical.solve")} <ArrowRight size={13} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <div className="grid-card p-5 space-y-4 border-l-4 border-l-blue-600" data-testid="clinical-fix-form">
          <div>
            <h3 className="text-sm font-bold">{t("clinical.fix_title")}</h3>
            <p className="text-xs text-zinc-600 mt-1">{t("clinical.fix_help")}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ClinicalSelect id="has_clinical_data" label={t("wiz.f.has_clinical")} value={draft.has_clinical_data} onChange={updateField} t={t} />
            <ClinicalSelect id="literature_review" label={t("wiz.f.literature")} value={draft.literature_review} onChange={updateField} t={t} />
            <ClinicalSelect id="equivalent_device" label={t("wiz.f.equiv")} value={draft.equivalent_device} onChange={updateField} t={t} />
            <ClinicalSelect id="pmcf_plan" label={t("wiz.f.pmcf")} value={draft.pmcf_plan} onChange={updateField} t={t} />
            <ClinicalText id="target_population" label={t("wiz.f.population")} value={draft.target_population} onChange={updateField} />
            <ClinicalText id="intended_clinical_benefit" label={t("wiz.f.benefit")} value={draft.intended_clinical_benefit} onChange={updateField} />
            {draft.equivalent_device === "yes" && (
              <>
                <ClinicalSelect id="equivalence_clinical" label={t("wiz.f.equiv_c")} value={draft.equivalence_clinical} onChange={updateField} t={t} />
                <ClinicalSelect id="equivalence_technical" label={t("wiz.f.equiv_t")} value={draft.equivalence_technical} onChange={updateField} t={t} />
                <ClinicalSelect id="equivalence_biological" label={t("wiz.f.equiv_b")} value={draft.equivalence_biological} onChange={updateField} t={t} />
              </>
            )}
          </div>

          {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm" role="alert" data-testid="clinical-fix-error">{error}</div>}
          <div className="flex flex-wrap justify-end gap-2 border-t border-zinc-100 pt-4">
            <button type="button" onClick={() => setEditing(false)} disabled={reevaluating} className="border border-zinc-200 px-4 py-2 rounded-sm text-sm font-semibold hover:bg-zinc-50">
              {t("qms.cancel")}
            </button>
            <button type="button" onClick={reevaluate} disabled={reevaluating || !onReevaluate} data-testid="clinical-reevaluate" className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50">
              {reevaluating ? t("clinical.reevaluating") : t("clinical.reevaluate")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function targetForGap(topic = "") {
  if (topic === "Clinical Benefit") return "intended_clinical_benefit";
  if (topic === "Target Population") return "target_population";
  if (topic === "Equivalence Demonstration") return "equivalence_clinical";
  return "has_clinical_data";
}

function ClinicalSelect({ id, label, value, onChange, t }) {
  return (
    <div>
      <label htmlFor={`clinical-fix-${id}`} className="text-xs font-semibold tracking-wide uppercase text-zinc-600">{label}</label>
      <select
        id={`clinical-fix-${id}`}
        value={value || "no"}
        onChange={(e) => onChange(id, e.target.value)}
        data-testid={`clinical-fix-${id}`}
        className="mt-1 w-full border border-zinc-200 px-3 py-2 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white"
      >
        <option value="yes">{t("wiz.yes")}</option>
        <option value="no">{t("wiz.no")}</option>
      </select>
    </div>
  );
}

function ClinicalText({ id, label, value, onChange }) {
  return (
    <div>
      <label htmlFor={`clinical-fix-${id}`} className="text-xs font-semibold tracking-wide uppercase text-zinc-600">{label}</label>
      <input
        id={`clinical-fix-${id}`}
        value={value || ""}
        onChange={(e) => onChange(id, e.target.value)}
        data-testid={`clinical-fix-${id}`}
        className="mt-1 w-full border border-zinc-200 px-3 py-2 rounded-sm focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm"
      />
    </div>
  );
}

function Stat({ label, value, ok, highlight }) {
  let color = "text-zinc-900";
  if (ok === true) color = "text-emerald-600";
  if (ok === false) color = "text-red-600";
  if (highlight === "insufficient") color = "text-red-600";
  if (highlight === "partial") color = "text-amber-600";
  if (highlight === "sufficient") color = "text-emerald-600";
  return (
    <div className="grid-card p-5">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`font-bold text-xl mt-2 capitalize ${color}`}>{value}</div>
    </div>
  );
}
